/**
 * Structured addresses for companies and for an owner's home.
 *
 * Pure rules shared by the API (which refuses bad input) and the forms (which
 * show the same message under the same field). A company has one `company`
 * address; an owner may also have an `owner_home` address, which carries how it
 * was confirmed because a mailed packet to the wrong house is not recoverable.
 */
import { stateCode } from "./usStates";
import { todayKey, CENTRAL_TZ } from "./weeks";

export type AddressKind = "company" | "owner_home";
export type Verification = "county_records" | "provided" | "county_mismatch";

export const VERIFICATION_LABEL: Record<Verification, string> = {
  county_records: "Confirmed in county records",
  provided: "Provided, not checked",
  county_mismatch: "Checked, not confirmed",
};

export interface AddressParts {
  street: string;
  line2: string | null;
  city: string;
  state: string | null;
  postalCode: string | null;
  country: string; // ISO alpha-2
}

export interface HomeDetails {
  addressee: string | null;
  verification: Verification;
  verificationNote: string | null;
  verifiedOn: string | null; // YYYY-MM-DD
}

export type ValidAddress = AddressParts & Partial<HomeDetails>;

export interface Address extends ValidAddress {
  kind: AddressKind;
  updatedAt: string;
  updatedBy: { id: string; name: string } | null;
}

export type HomeStatus = "none" | "provided" | "county_mismatch";
export type MailTo =
  | { to: "owner_home" }
  | { to: "company"; home: HomeStatus }
  | { to: null; home: HomeStatus };

type Result = { ok: true; value: ValidAddress } | { ok: false; field: string; message: string };

const MAX = 200;
const MAX_NOTE = 1000;
const ZIP = /^\d{5}(-\d{4})?$/;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Anything that is not a string reads as blank; blank reads as null. */
function clean(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t === "" ? null : t;
}

function normalizeCountry(v: unknown): string | null {
  const t = clean(v);
  if (t === null) return "US";
  const u = t.toUpperCase();
  if (u === "US" || u === "USA" || u === "UNITED STATES") return "US";
  return /^[A-Z]{2}$/.test(u) ? u : null;
}

function validDate(s: string): boolean {
  const m = DATE.exec(s);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}

export function validateAddress(input: unknown, kind: AddressKind): Result {
  const raw = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const fail = (field: string, message: string): Result => ({ ok: false, field, message });
  const tooLong = (v: string | null, max: number) => v !== null && v.length > max;

  const street = clean(raw.street);
  if (!street) return fail("street", "Street is required.");
  if (tooLong(street, MAX)) return fail("street", "Street is too long.");

  const line2 = clean(raw.line2);
  if (tooLong(line2, MAX)) return fail("line2", "Suite is too long.");

  const city = clean(raw.city);
  if (!city) return fail("city", "City is required.");
  if (tooLong(city, MAX)) return fail("city", "City is too long.");

  const country = normalizeCountry(raw.country);
  if (country === null) return fail("country", "Country must be a two-letter code, like US or CA.");

  let state = clean(raw.state);
  let postalCode = clean(raw.postalCode);
  if (country === "US") {
    state = stateCode(state);
    if (state === null) return fail("state", "Use a US state, like TX or Texas.");
    if (postalCode === null || !ZIP.test(postalCode)) {
      return fail("postalCode", "ZIP must be 5 digits, or ZIP+4 like 74101-1234.");
    }
  } else {
    if (tooLong(state, MAX)) return fail("state", "State is too long.");
    if (tooLong(postalCode, MAX)) return fail("postalCode", "ZIP is too long.");
  }

  const value: ValidAddress = { street, line2, city, state, postalCode, country };
  if (kind === "company") return { ok: true, value };

  const verification = raw.verification;
  if (verification !== "county_records" && verification !== "provided" && verification !== "county_mismatch") {
    return fail("verification", "Say how this home address was confirmed.");
  }
  const addressee = clean(raw.addressee);
  if (tooLong(addressee, MAX)) return fail("addressee", "Addressee is too long.");
  const verificationNote = clean(raw.verificationNote);
  if (tooLong(verificationNote, MAX_NOTE)) return fail("verificationNote", "Note is too long.");
  const verifiedOn = clean(raw.verifiedOn);
  if (verifiedOn !== null && !validDate(verifiedOn)) return fail("verifiedOn", "Date checked must be YYYY-MM-DD.");
  // Keys compare as text; today is Central's, since the container runs UTC.
  if (verifiedOn !== null && verifiedOn > todayKey(new Date(), CENTRAL_TZ)) {
    return fail("verifiedOn", "Date checked can't be in the future.");
  }

  return { ok: true, value: { ...value, addressee, verification, verificationNote, verifiedOn } };
}

/** USPS-style lines: addressee, street, suite, "City, ST ZIP", and a country only outside the US. */
export function formatAddressLines(a: AddressParts, opts?: { addressee?: string | null }): string[] {
  const lines: string[] = [];
  const addressee = clean(opts?.addressee);
  if (addressee) lines.push(addressee);
  lines.push(a.street);
  if (a.line2) lines.push(a.line2);
  const cityState = a.state ? `${a.city}, ${a.state}` : a.city;
  lines.push([cityState, a.postalCode].filter(Boolean).join(" "));
  if (a.country !== "US") lines.push(a.country);
  return lines;
}

export function mailToLabel(m: MailTo): string {
  if (m.to === "owner_home") return "Home";
  if (m.to === "company") {
    if (m.home === "provided") return "Company (home address not checked yet)";
    if (m.home === "county_mismatch") return "Company (home address not confirmed)";
    return "Company (no home address)";
  }
  if (m.home === "provided") return "Check the home address in county records, or add a company address";
  return "No mailing address yet";
}

const COUNTRY_TOKEN = /^(united states|us|usa)$/i;
const LEADING_SUITE = /^(suite|ste|unit|floor|fl|#)\b/i;
/** A city token holding one of these is a suite or building line, so the text is not read. */
const UNIT_WORD = /\b(building|bldg|level|floor|fl|suite|ste|unit|apt|room|rm|dept)\b/i;
const STATE_ZIP = /^(.+?)\s+(\d{5}(?:-\d{4})?)$/;

function addressTokens(text: string | null | undefined): string[] {
  if (typeof text !== "string") return [];
  return text.split(",").map((t) => t.trim()).filter((t) => t !== "" && !COUNTRY_TOKEN.test(t));
}

const words = (tokens: string[]) => tokens.join(" ").toLowerCase().split(/\s+/).filter(Boolean);

/**
 * Reads a legacy free-text US address, or returns null. Accepts only
 * `street, city, state, ZIP` or `street, city, "state ZIP"` (state as a code or
 * a name), after dropping country tokens and a trailing repeat of city/state/ZIP,
 * with an optional suite token before or right after the street. A city holding
 * a unit word ("Building A", "Lower Level") is null. Anything else is null: a
 * wrong address migrated quietly is worse than one left for a person.
 */
export function parseAddressText(text: string | null | undefined): AddressParts | null {
  let tokens = addressTokens(text);
  let line2: string | null = null;
  if (tokens.length > 1 && LEADING_SUITE.test(tokens[0])) {
    line2 = tokens[0];
    tokens = tokens.slice(1);
  } else if (tokens.length > 3 && LEADING_SUITE.test(tokens[1])) {
    line2 = tokens[1];
    tokens = [tokens[0], ...tokens.slice(2)];
  }
  if (tokens.length < 3) return null;

  for (const n of [4, 3]) {
    if (tokens.length < n) continue;
    const head = tokens.slice(0, n);
    const [street, city] = head;
    let stateRaw: string;
    let zip: string;
    if (n === 4) {
      stateRaw = head[2];
      zip = head[3];
    } else {
      const m = STATE_ZIP.exec(head[2]);
      if (!m) continue;
      stateRaw = m[1];
      zip = m[2];
    }
    const state = stateCode(stateRaw);
    if (state === null || !ZIP.test(zip)) continue;
    if (!/\d/.test(street) || street === "-" || LEADING_SUITE.test(street)) continue;
    if (!city || /\d/.test(city) || UNIT_WORD.test(city)) continue;

    const tail = words(head.slice(1));
    const rest = words(tokens.slice(n));
    const repeats =
      rest.length === 0 ||
      rest.join(" ") === tail.join(" ") ||
      rest.join(" ") === tail.slice(0, -1).join(" ");
    if (!repeats) continue;
    return { street, line2, city, state, postalCode: zip, country: "US" };
  }
  return null;
}

/** True for text with no street: only a city and state, often repeated. */
export function looksCityStateOnly(text: string | null | undefined): boolean {
  const tokens = addressTokens(text);
  if (tokens.length < 2) return false;
  if (tokens.some((t) => /\d/.test(t))) return false;
  return tokens.some((t) => stateCode(t) !== null);
}

/** Home only when county records confirmed it; otherwise the company, else nowhere. */
export function packetMailTo(a: {
  company: AddressParts | null;
  ownerHome: { verification: Verification } | null;
}): MailTo {
  const v = a.ownerHome?.verification;
  if (v === "county_records") return { to: "owner_home" };
  const home: HomeStatus = v ?? "none";
  return a.company ? { to: "company", home } : { to: null, home };
}
